import { memo, useCallback } from 'react';
import { Dot, TrashIcon } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import { Button } from '@/shared/components/ui/button';
import { usePageArchiveMutations } from '@/features/pages/hooks/usePageArchiveMutations';
import { usePageSelection } from '@/features/pages/hooks/usePageSelection';
import { usePageStore } from '@/features/pages/stores/usePageStore';
import { useSyncStore } from '@/features/sync/stores/useSyncStore';
import { PageMetadata } from '@/features/pages/types/page.types';
import { fetchCollectionStructure } from '@/features/collections/hooks/useCollectionsQuery';
import { collectionKeys } from '@/shared/query/queryKeys';

const ListItem = ({ metadata }: { metadata: PageMetadata }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { archivePage } = usePageArchiveMutations();
  const { setCurrentPageId } = usePageSelection();
  const location = useLocation();
  const currentId = usePageStore(state => state.currentPageId || '');
  const editable = usePageStore(state => state.editable);
  const lastSyncedAt = useSyncStore(state => state.lastSyncedAt);

  const isUnsynced = !!lastSyncedAt && lastSyncedAt < metadata.updatedAt;
  const isSelected = metadata.id === currentId;

  const isShared = location.pathname.includes('/shared');

  const onClick = () => {
    if (isShared && metadata.collectionId) {
      navigate(`/shared/${metadata.collectionId}/page/${metadata.id}`);
      return;
    }
    if (metadata.collectionId) {
      navigate(`/collection/${metadata.collectionId}/page/${metadata.id}`);
      return;
    }
    navigate(`/page/${metadata.id}`);
  };

  const goToPage = useCallback(async () => {
    const id = await setCurrentPageId();
    if (id) {
      navigate(`/page/${id}`);
    }
  }, [navigate, setCurrentPageId]);

  const onArchive = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      await archivePage(metadata.id, metadata.collectionId);
      // Don't navigate if we're not on the current page
      if (currentId !== metadata.id) return;

      if (metadata.collectionId) {
        // Navigate to a sibling page in the same section, or the collection root
        const structure = await queryClient.fetchQuery({
          queryKey: collectionKeys.structure(metadata.collectionId),
          queryFn: () => fetchCollectionStructure(metadata.collectionId as string),
        });
        const collectionSection = structure.find(s => s.section.id === metadata.sectionId);
        const sectionPageIds = collectionSection?.section.pages
          .map(p => p.id)
          .filter(pId => pId !== metadata.id);
        if (sectionPageIds?.length) {
          navigate(`/collection/${metadata.collectionId}/page/${sectionPageIds[0]}`);
        } else {
          navigate(`/collection/${metadata.collectionId}`);
        }
      } else {
        await goToPage();
      }
    },
    [
      archivePage,
      metadata.id,
      metadata.collectionId,
      metadata.sectionId,
      currentId,
      queryClient,
      navigate,
      goToPage,
    ]
  );

  return (
    <div className="w-full group">
      <div
        title={`"${metadata.title}"`}
        onClick={onClick}
        className={cn(
          'mx-2 flex items-center rounded hover:bg-accent cursor-pointer h-9 p-1',
          isSelected ? 'bg-accent text-accent-foreground' : ''
        )}
      >
        <div className="flex flex-1 items-center flex-grow overflow-hidden">
          <span className="mr-1 text-[14px]">📄</span>
          <span className="text-[14px] truncate">{metadata.title}</span>
        </div>
        {!metadata.archivedAt && editable && !isShared && (
          <Button
            variant="ghost"
            size="icon"
            className="h-[calc(100%-0.5px)] w-8"
            onClick={onArchive}
          >
            {isUnsynced && (
              <Dot className="block group-hover:hidden h-6 w-6 text-secondary-foreground" />
            )}
            <TrashIcon className="hidden group-hover:block transition-opacity h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default memo(ListItem);
